import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { groupBy } from 'lodash-es';
import Link from 'next/link';
import dynamic from 'next/dynamic';

import {
  MostPreferredToursViewModel,
  WishListItemViewModel,
  WishListViewModel,
} from '@api/v3';
import { FlexibleDiv } from '@components/formStyles';
import Typography from '@components/Typography';
import { useDevice } from '@contexts/device';
import CustomIcon from '@components/utils/CustomIcon';
import { RightArrowIcon } from '@assets/icons';
import {
  tourMostPreferred,
  shipMostPreferred,
} from '@utils/newDataLayer/productMostPreferred';
import { productImpression } from '@utils/newDataLayer/productImpression';
import IsInViewPort from '@components/ui/IsInViewPort';

import { Container, Content, FlexWrapper, ShowAllItems } from './styled';
import ContentFilterTabs from '../common/FilterTabs';
import { ContentWrapper } from '../MostPreferredHotels/styled';

const FeaturedTourDesktop = dynamic(() => import('./FeaturedTourDesktop'), {
  ssr: true,
});

const FeaturedTourMobile = dynamic(() => import('./FeaturedTourMobile'), {
  ssr: true,
});

type Props = {
  data: MostPreferredToursViewModel;
  favoriteLists?: WishListViewModel[];
  refetchFavoriteLists?: any;
  defaultList?: WishListViewModel;
  defaultListItems?: WishListItemViewModel[];
  refetchDefaultList?: any;
  isRedirectingSet?: Dispatch<SetStateAction<boolean>>;
};

const FeaturedTours = ({
  data,
  favoriteLists,
  refetchFavoriteLists,
  defaultList,
  defaultListItems,
  refetchDefaultList,
  isRedirectingSet,
}: Props) => {
  const { isDesktop } = useDevice();

  const [selectedTourCategory, setSelectedTourCategory] = useState(
    data?.categories?.[0]?.title ?? ''
  );

  const formattedData = useMemo(() => {
    const groupedItems = groupBy(data.items, 'category');

    return {
      categories: data?.categories?.filter((item) =>
        Object.keys(groupedItems)?.includes(item?.title ?? '')
      ),
      activeCategoryItems: groupedItems[selectedTourCategory],
    };
  }, [data, selectedTourCategory]);

  const activeCategoryLink = useMemo(() => {
    const activeCategoryItem =
      data?.categories?.find((item) => item.title === selectedTourCategory) ??
      data?.categories?.[0];

    return data?.links?.[data?.categories?.indexOf(activeCategoryItem)];
  }, [data, selectedTourCategory]);

  return (
    <Container>
      <FlexibleDiv bottom={12}>
        <Typography weight='Black' color='BR300' variant='Title3'>
          {data?.title}
        </Typography>
      </FlexibleDiv>
      <Content>
        <FlexibleDiv bottom={isDesktop ? 20 : 12}>
          <FlexWrapper>
            <ContentFilterTabs
              tabs={formattedData?.categories ?? []}
              defaultSelectedTabKey={selectedTourCategory}
              onTabSelected={(selected) => setSelectedTourCategory(selected)}
            />

            {activeCategoryLink?.href && isDesktop ? (
              <Link
                href={activeCategoryLink?.href}
                prefetch={false}
                onClick={() => {
                  isRedirectingSet?.(true);
                  activeCategoryLink?.name === 'Tüm Gemi Turları'
                    ? productImpression({
                        item_list_location:
                          'Anasayfa En Çok Tercih Edilen Gemi Turları',
                        item_list_id:
                          activeCategoryLink?.name || 'Tüm Gemi Turları',
                        type: 'ship',
                      })
                    : productImpression({
                        item_list_location:
                          'Anasayfa En Çok Tercih Edilen Turlar',
                        item_list_id: activeCategoryLink?.name || 'Tüm Turlar',
                        type: 'tour',
                      });
                }}
              >
                <ShowAllItems>
                  <Typography variant='caption' weight='SemiBold' color='BR300'>
                    {activeCategoryLink?.name}
                  </Typography>

                  <CustomIcon
                    fontSize={24}
                    icon={RightArrowIcon}
                    paletteColor='BR300'
                    style={{ marginBottom: '-4px' }}
                  />
                </ShowAllItems>
              </Link>
            ) : null}
          </FlexWrapper>
        </FlexibleDiv>

        {formattedData?.activeCategoryItems?.length ? (
          <IsInViewPort
            key={activeCategoryLink?.name || 'tour'}
            onViewVisible={() => {
              formattedData?.activeCategoryItems[0]?.category === 'Gemi'
                ? shipMostPreferred(formattedData?.activeCategoryItems, false)
                : tourMostPreferred(formattedData?.activeCategoryItems, false);
            }}
          >
            {isDesktop ? (
              <FeaturedTourDesktop
                favoriteLists={favoriteLists}
                refetchFavoriteLists={refetchFavoriteLists}
                defaultList={defaultList}
                defaultListItems={defaultListItems}
                refetchDefaultList={refetchDefaultList}
                isRedirectingSet={isRedirectingSet}
                formattedData={formattedData}
              />
            ) : (
              <FeaturedTourMobile
                favoriteLists={favoriteLists}
                refetchFavoriteLists={refetchFavoriteLists}
                defaultList={defaultList}
                defaultListItems={defaultListItems}
                refetchDefaultList={refetchDefaultList}
                isRedirectingSet={isRedirectingSet}
                formattedData={formattedData}
              />
            )}
          </IsInViewPort>
        ) : null}
      </Content>

      {activeCategoryLink?.href && !isDesktop ? (
        <Link
          href={activeCategoryLink?.href}
          onClick={() => {
            isRedirectingSet?.(true);
          }}
          prefetch={false}
        >
          <ShowAllItems>
            <Typography variant='caption' weight='SemiBold' color='BR300'>
              {activeCategoryLink?.name}
            </Typography>

            <CustomIcon
              fontSize={24}
              icon={RightArrowIcon}
              paletteColor='BR300'
              style={{ marginBottom: '-4px' }}
            />
          </ShowAllItems>
        </Link>
      ) : null}

      {data?.content ? (
        <ContentWrapper>
          <FlexibleDiv left={isDesktop ? 0 : 15}>
            <Typography variant='caption2' color='D100' weight='Regular'>
              {data?.content}
            </Typography>
          </FlexibleDiv>
        </ContentWrapper>
      ) : (
        ''
      )}
    </Container>
  );
};

export default FeaturedTours;
